interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

interface TagWithColor {
  color: string;
}

interface BlendResult {
  hex: string;
  three: number;
}

const DEFAULT_NODE_RGB: RGB = { r: 92, g: 88, b: 78 };
export const DEFAULT_NODE_COLOR_HEX = '#5C584E';
export const DEFAULT_NODE_COLOR_THREE = 0x5C584E;

export function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    console.warn(`hexToRgb: invalid hex "${hex}", using default`);
    return { ...DEFAULT_NODE_RGB };
  }
  return { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) };
}

export function rgbToHex({ r, g, b }: RGB): string {
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export function hexToThree(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

export function blendTagColors(tags: TagWithColor[]): BlendResult {
  if (!tags || tags.length === 0) {
    return { hex: DEFAULT_NODE_COLOR_HEX, three: DEFAULT_NODE_COLOR_THREE };
  }
  if (tags.length === 1) {
    return { hex: tags[0].color, three: hexToThree(tags[0].color) };
  }
  let r = 0, g = 0, b = 0;
  for (const tag of tags) {
    const c = hexToRgb(tag.color);
    r += c.r; g += c.g; b += c.b;
  }
  const hex = rgbToHex({ r: r / tags.length, g: g / tags.length, b: b / tags.length });
  return { hex, three: hexToThree(hex) };
}

export function getNodeColorFromTags(tags: TagWithColor[], format: 'three' | 'hex' = 'three'): string | number {
  const blended = blendTagColors(tags);
  return format === 'hex' ? blended.hex : blended.three;
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6; break;
      case gn: h = ((bn - rn) / d + 2) / 6; break;
      case bn: h = ((rn - gn) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToRgb({ h, s, l }: HSL): RGB {
  const hn = h / 360, sn = s / 100, ln = l / 100;
  if (sn === 0) {
    const v = Math.round(ln * 255);
    return { r: v, g: v, b: v };
  }
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  return {
    r: Math.round(hue2rgb(p, q, hn + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hn) * 255),
    b: Math.round(hue2rgb(p, q, hn - 1 / 3) * 255),
  };
}

function perceptualLabelLightness(hue: number): number {
  const hRad = (hue - 240) * Math.PI / 180;
  const base = 75;
  const adjustment = 8 * Math.cos(hRad);
  return Math.min(90, Math.max(68, base + adjustment));
}

export function getLabelColor(nodeHex: string): string {
  if (nodeHex.toUpperCase() === DEFAULT_NODE_COLOR_HEX) return nodeHex;
  const rgb = hexToRgb(nodeHex);
  const hsl = rgbToHsl(rgb);
  hsl.l = perceptualLabelLightness(hsl.h);
  hsl.s = Math.min(70, hsl.s + 5);
  return rgbToHex(hslToRgb(hsl));
}
